import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Paper,
  Tab,
  Tabs,
  Grid,
  CircularProgress,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFTextField,
  RHFDateTimePicker,
  RHFTimePicker,
} from "../../../../RHFComponents/RHFComponents";
import { MedintDataContext } from "../../../Context/MedintContextWrapper";
import { medboxd } from "../../../../../networking";
import dayjs, { Dayjs } from "dayjs";
import { AllertMessage } from "../MedintDetails";

type MedintBoxDetailModalProps = {
  open: boolean;
  onBack: () => void;
  onClose?: () => void;
  onConfirm?: (data: BoxDetailFormValues) => void;
  mode?: "create" | "edit";
  initialValues?: Partial<BoxDetailFormValues>;
};

export type BoxDetailFormValues = {
  medicoRif: string;

  dataInizio: string | null;
  oraInizio: string | null;
  dataFine: string | null;
  oraFine: string | null;

  codiceMedico: string | null;
  ambulatorio: string | null;

  dataPrevistaRitiroReferto: string | null;

  dataInserimento: string | null;
  oraInserimento: string | null;
  operatoreInserimento: string | null;

  dataModifica: string | null;
  oraModifica: string | null;
  operatoreModifica: string | null;
  MEDBOX_PRGINT?: string;
  medboxdPrgint?: string;
};

const BLUE = "#003267";

/* Converte una data (Dayjs/Date/string) nel formato richiesto dall’API, evitando invii ambigui o “Invalid Date”.*/
export function formatFormDateForApi(
  formDate: string | Dayjs | Date | null | undefined,
  inputFormat: string = "DD/MM/YY",
  apiFormat: string = "YYYY-MM-DD",
): string {
  if (!formDate) return "";

  const parsed = dayjs.isDayjs(formDate)
    ? formDate
    : formDate instanceof Date
      ? dayjs(formDate)
      : dayjs(formDate, inputFormat, true);

  return parsed.isValid() ? parsed.format(apiFormat) : "";
}

export default function MedintBoxDetailModal({
  open,
  onBack,
  onClose,
  onConfirm,
  mode = "create",
  initialValues,
}: MedintBoxDetailModalProps) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  //Stati per la gestione della componente dell'allert
  const [showAllert, setShowAllert] = useState<boolean>(false);
  const [severity, setSeverity] = useState<string>("warning");
  const [message, setMessage] = useState<string>(
    "Si è verificato un problema sulla pagina",
  );
  const context = useContext(MedintDataContext);

  const agendaOptions = useMemo(
    () => context?.doctorAgenda?.agende?.agenda ?? [],
    [context?.doctorAgenda],
  );

  const methods = useForm<BoxDetailFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      medicoRif: "",
      dataInizio: null,
      oraInizio: null,
      dataFine: null,
      oraFine: null,
      codiceMedico: null,
      ambulatorio: null,
      dataPrevistaRitiroReferto: null,
      dataInserimento: null,
      oraInserimento: null,
      operatoreInserimento: "",
      dataModifica: null,
      oraModifica: null,
      operatoreModifica: "",
      MEDBOX_PRGINT: "",
    },
  });

  useEffect(() => {
    if (!open) return;

    const m = context?.doctorAgenda?.medico?.[0];

    console.log({ mode });

    const medicoFullName = m
      ? `${m.COGNOMEMEDICO ?? ""} ${m.NOMEMEDICO ?? ""}`.trim()
      : "";

    const medicoCode = m?.CODICEMEDICO ? String(m.CODICEMEDICO) : null;

    methods.reset({
      medicoRif: medicoFullName,
      codiceMedico: medicoCode,
      ...initialValues,
    });
  }, [open, context?.doctorAgenda, initialValues, methods]);

  console.log({ initialValues });

  const handleSubmit = methods.handleSubmit(async (data) => {
    const values = methods.getValues();
    console.log("oraInizio raw:", data.oraInizio);
    console.log("oraFine raw:", data.oraFine);

    setLoading(true);
    try {
      const payload = {
        crud: mode === "create" ? "c" : "u",
        ...(mode === "edit" ? { prgint: data.medboxdPrgint || "" } : {}),
        dati: {
          medboxd: {
            MEDBOX_PRGINT: context?.doctorAgenda.medico[0].IDMEDICO ?? "",
            MEDBOXD_ORA_INI: data.oraInizio,
            MEDBOXD_ORA_FINE: data.oraFine,
            MEDINT_CODICE: data.codiceMedico ?? "",
            MEDBOXD_GG_RITIRO: "0",
            TABAMB_CODICE: data.ambulatorio ?? "",
            MEDBOXD_DATA_INI: data.dataInizio,
            MEDBOXD_DATA_FINE: data.dataFine,
            MEDBOXD_DT_RITIRO: data.dataPrevistaRitiroReferto,
          },
        },
      };
      const response = await medboxd(payload);
      console.log({ response });
      onConfirm?.(data);
      const medicoRiferimento = methods.getValues("medicoRif");
      const codiceMedico = methods.getValues("codiceMedico");
      methods.reset({
        medicoRif: medicoRiferimento,
        codiceMedico: codiceMedico,
        dataInizio: null,
        oraInizio: null,
        dataFine: null,
        oraFine: null,
        ambulatorio: null,
        dataPrevistaRitiroReferto: null,
        dataInserimento: null,
        oraInserimento: null,
        operatoreInserimento: "",
        dataModifica: null,
        oraModifica: null,
        operatoreModifica: "",
        MEDBOX_PRGINT: "",
        medboxdPrgint: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  });

  return (
    <>
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
      />

      <Modal open={open} onClose={onClose ?? onBack}>
        <FormProvider {...methods}>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              fontFamily: "Montserrat",
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: "min(980px, 96vw)",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                borderRadius: 1,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(255,255,255,0.85)",
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={48} />
                </Box>
              )}

              <Box sx={{ px: 3, pt: 2 }}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  aria-label="Box Dettaglio"
                  sx={{
                    minHeight: 38,
                    "& .MuiTabs-indicator": {
                      backgroundColor: BLUE,
                      height: 3,
                      borderRadius: 3,
                    },
                    "& .MuiTab-root": {
                      minHeight: 38,
                      textTransform: "none",
                      color: `${BLUE} !important`,
                      px: 0,
                      mr: 3,
                      fontFamily: "Montserrat",
                      fontWeight: "bold",
                    },
                    "& .Mui-selected": { color: BLUE },
                  }}
                >
                  <Tab id="boxDettaglio" label="Box - Dettaglio" />
                </Tabs>
              </Box>

              <Box sx={{ p: 3, overflowY: "auto", flex: 1, minHeight: 0 }}>
                <Grid container spacing={2} sx={{ width: "min(860px, 100%)" }}>
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      control={methods.control}
                      name="medicoRif"
                      label="Medico / Risorsa di Riferimento"
                      disabled
                      placeholder="Medico"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6} />

                  <Grid item xs={12} md={6}>
                    <RHFDateTimePicker
                      control={methods.control}
                      name="dataInizio"
                      label="Data Inizio"
                      dateOnly
                      serialize="string"
                      fullWidth
                      displayFormat="DD/MM/YY"
                    valueFormat="YYYY-MM-DD"
                      rules={{ required: "Data obbligatoria" }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RHFTimePicker
                      control={methods.control}
                      name="oraInizio"
                      label="Ora Inizio"
                      serialize="string"
                      fullWidth
                      minutesStep={5}
                      rules={{ required: "Ora obbligatoria" }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RHFDateTimePicker
                      control={methods.control}
                      name="dataFine"
                      label="Data Fine"
                      dateOnly
                      serialize="string"
                      displayFormat="DD/MM/YY"
                      valueFormat="YYYY-MM-DD"
                      fullWidth
                      rules={{
                        required: "Data fine obbligatoria",
                        // validate: (v) => {
                        //   const ini = methods.getValues("dataInizio");
                        //   if (!v || !ini) return true;
                        //   const dIni = dayjs(ini, "DD/MM/YY", true);
                        //   const dFine = dayjs(v, "DD/MM/YY", true);
                        //   return (
                        //     dFine.isSameOrAfter(dIni, "day") ||
                        //     "La data fine deve essere >= data inizio"
                        //   );
                        // },
                      }}
                  />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RHFTimePicker
                      control={methods.control}
                      name="oraFine"
                      label="Ora Fine"
                      serialize="string"
                      fullWidth
                      minutesStep={5}
                      rules={{ required: "Ora obbligatoria" }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RHFAutocomplete
                      control={methods.control}
                      name="codiceMedico"
                      label="Codice Medico"
                      options={context?.doctorsData ?? []}
                      keyField="IDMEDICO"
                      getOptionLabel={(d: any) =>
                        `${d.CODICEMEDICO ?? d["codice medico"] ?? ""} - ${
                          d.COGNOMEMEDICO ?? d.cognome ?? ""
                        } ${d.NOMEMEDICO ?? d.nome ?? ""}`.trim()
                      }
                      mapIn={(code, opts) =>
                        opts.find(
                          (o: any) =>
                            String(
                              o.CODICEMEDICO ?? o?.["codice medico"] ?? "",
                            ) === String(code),
                        ) ?? null
                      }
                      mapOut={(opt: any) =>
                        String(
                          opt?.CODICEMEDICO ?? opt?.["codice medico"] ?? "",
                        ) || null
                      }
                    />
                  </Grid>

                <Grid item xs={12} md={6}>
                  <RHFAutocomplete
                    control={methods.control}
                    name="ambulatorio"
                    label="Ambulatorio"
                    options={agendaOptions}
                    keyField="CODICEPGMEDT"
                    getOptionLabel={(a: any) => `${a.DSCPGMEDT ?? ""}`.trim()}
                    mapIn={(code, opts) =>
                      opts.find(
                        (o: any) => String(o.CODICEPGMEDT) === String(code),
                      ) ?? null
                    }
                    mapOut={(opt: any) =>
                      opt ? String(opt.CODICEPGMEDT) : null
                    }
                  />
                </Grid>

                  <Grid item xs={12} md={6}>
                    <RHFDateTimePicker
                      control={methods.control}
                      name="dataPrevistaRitiroReferto"
                      label="Data prevista ritiro referto"
                      dateOnly
                      serialize="string"
                      fullWidth
                      displayFormat="DD/MM/YY"
                      rules={{ required: "Data obbligatoria" }}
                      valueFormat="YYYY-MM-DD"
                  />
                  </Grid>

                  <Grid item xs={12} md={6} />
                </Grid>

                <Paper
                  variant="outlined"
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <RHFDateTimePicker
                        control={methods.control}
                        name="dataInserimento"
                        label="Data Inserimento"
                        dateOnly
                        serialize="string"
                        fullWidth
                        displayFormat="DD/MM/YY"
                        rules={{ required: "Data obbligatoria" }}
                        valueFormat="YYYY-MM-DD"
                    />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFTimePicker
                        control={methods.control}
                        name="oraInserimento"
                        label="Ora Inserimento"
                        serialize="string"
                        fullWidth
                        rules={{ required: "Ora obbligatoria" }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFTextField
                        control={methods.control}
                        name="operatoreInserimento"
                        label="Operatore Inserimento"
                        placeholder="Placeholder"
                        fullWidth
                        rules={{ required: "Operatore obbligatorio" }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFDateTimePicker
                        control={methods.control}
                        name="dataModifica"
                        label="Data Modifica"
                        dateOnly
                        serialize="string"
                        fullWidth
                        displayFormat="DD/MM/YY"
                        rules={{ required: "Data obbligatoria" }}
                        valueFormat="YYYY-MM-DD"
                    />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFTimePicker
                        control={methods.control}
                        name="oraModifica"
                        label="Ora Modifica"
                        serialize="string"
                        fullWidth
                        rules={{ required: "Ora obbligatoria" }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFTextField
                        control={methods.control}
                        name="operatoreModifica"
                        label="Operatore Modifica"
                        placeholder="Placeholder"
                        fullWidth
                        rules={{ required: "Operatore obbligatorio" }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlinedCustom"
                  color="primary"
                  onClick={onBack}
                  disabled={loading}
                  sx={{
                    borderRadius: 1,
                    fontWeight: "bold",
                    px: 2.5,
                  }}
                >
                  Indietro
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    borderRadius: 1,
                    fontWeight: "bold",
                    px: 2.5,
                    backgroundColor: "#2E83C4",
                    "&:hover": { backgroundColor: "#276fa5" },
                  }}
                >
                  Conferma
                </Button>
              </Box>
            </Paper>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
}
